import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface CommonResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { question: string; answer: string }) => void;
  response?: any;
  isLoading?: boolean;
}

export const CommonResponseModal = ({
  isOpen,
  onClose,
  onSave,
  response,
  isLoading = false,
}: CommonResponseModalProps) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');

  useEffect(() => {
    if (response) {
      setQuestion(response.commentExample || '');
      setAnswer(response.suggestedOutput || '');
    } else {
      setQuestion('');
      setAnswer('');
    }
  }, [response, isOpen]);

  const handleSave = () => {
    if (question.trim() && answer.trim()) {
      onSave({ question: question.trim(), answer: answer.trim() });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onKeyDown={handleKeyDown}
    >
      <div className="bg-grey rounded-xl p-6 w-full max-w-2xl mx-4 border border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            {response ? 'Edit Common Response' : 'Add Common Response'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            disabled={isLoading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Comment Example
            </label>
            <input
              type="text"
              className="bg-black border border-gray-600 rounded p-3 w-full text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet"
              placeholder="e.g. Do you ship internationally?"
              value={question}
              onChange={(e) => {
                if (e.target.value.length <= 250) setQuestion(e.target.value);
              }}
              disabled={isLoading}
              maxLength={250}
            />
            <div className="text-xs text-gray-400 text-right mt-1">
              {question.length}/250
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Suggested Response
            </label>
            <textarea
              className="bg-black border border-gray-600 rounded p-3 w-full text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet h-32"
              placeholder="e.g. Yes, we ship worldwide! 🌍 Just $15, arrives in 7–10 biz days"
              value={answer}
              onChange={(e) => {
                if (e.target.value.length <= 500) setAnswer(e.target.value);
              }}
              disabled={isLoading}
              maxLength={500}
            />
            <div className="text-xs text-gray-400 text-right mt-1">
              {answer.length}/500
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!question.trim() || !answer.trim() || isLoading}
            className={`px-4 py-2 rounded-xl font-semibold transition-colors ${
              !question.trim() || !answer.trim() || isLoading
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-yellow text-black hover:brightness-90'
            }`}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
